import { FC, useMemo } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

interface SpendCategoryOption {
  id: string;
  name: string;
  nameTree: string[];
}

type FlatOption = SpendCategoryOption & { depth: number; path: string };

interface SpendCategoryAutocompleteProps {
  categories: SpendCategoryOption[];
  value: string | undefined;
  onChange: (categoryId: string | undefined) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  /** Category IDs to exclude from the options list */
  excludeIds?: string[];
  /** data-testid forwarded to the underlying input element */
  testId?: string;
}

export const SpendCategoryAutocomplete: FC<SpendCategoryAutocompleteProps> = ({
  categories,
  value,
  onChange,
  label = "Spend category",
  placeholder = "Search spend categories",
  required = false,
  error = false,
  helperText,
  excludeIds = [],
  testId,
}) => {
  const flatOptions = useMemo<FlatOption[]>(() => {
    const mapped = categories
      .filter((c) => !excludeIds.includes(c.id))
      .map((c) => ({
        ...c,
        depth: Math.max((c.nameTree?.length ?? 1) - 1, 0),
        path: c.nameTree?.length ? c.nameTree.join(" > ") : c.name,
      }));
    return mapped.sort((a, b) => a.path.localeCompare(b.path));
  }, [categories, excludeIds]);

  const selectedOption = flatOptions.find((c) => c.id === value) ?? null;

  return (
    <Autocomplete<FlatOption>
      size="small"
      fullWidth
      options={flatOptions}
      value={selectedOption}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      getOptionLabel={(opt) => opt.path}
      onChange={(_, newValue) => onChange(newValue?.id)}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Box sx={{ pl: option.depth * 1.5 }}>
            <Typography variant="body2">{option.name}</Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          inputProps={{
            ...params.inputProps,
            ...(testId ? { "data-testid": testId } : {}),
          }}
        />
      )}
    />
  );
};
